import React, { useEffect, useMemo, useRef, useState } from "react";
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

// Minimal Domino SVG renderer
function DominoSVG({
  a,
  b,
  size = 64,
  vertical = false,
  className = "",
  style = {},
}) {
  const w = size;
  const h = Math.round(size * 0.55);
  const pipR = Math.max(1.6, size * 0.03);

  // unique ids for gradients/filters per instance to avoid collisions
  const gid = useMemo(
    () => "dom-grad-" + Math.random().toString(36).slice(2, 9),
    []
  );
  const fid = useMemo(
    () => "dom-filt-" + Math.random().toString(36).slice(2, 9),
    []
  );
  const hid = useMemo(
    () => "dom-high-" + Math.random().toString(36).slice(2, 9),
    []
  );
  const pipFid = useMemo(
    () => "dom-pipf-" + Math.random().toString(36).slice(2, 9),
    []
  );

  // detect dark mode (tailwind 'dark' class or prefers-color-scheme)
  const isDark =
    typeof window !== "undefined" &&
    ((window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches) ||
      document.documentElement.classList.contains("dark"));

  const faceToPips = (n, xOffset = 0) => {
    const coords = {
      0: [],
      1: [[0.5, 0.5]],
      2: [
        [0.25, 0.25],
        [0.75, 0.75],
      ],
      3: [
        [0.25, 0.25],
        [0.5, 0.5],
        [0.75, 0.75],
      ],
      4: [
        [0.25, 0.25],
        [0.25, 0.75],
        [0.75, 0.25],
        [0.75, 0.75],
      ],
      5: [
        [0.25, 0.25],
        [0.25, 0.75],
        [0.5, 0.5],
        [0.75, 0.25],
        [0.75, 0.75],
      ],
      6: [
        [0.25, 0.25],
        [0.25, 0.5],
        [0.25, 0.75],
        [0.75, 0.25],
        [0.75, 0.5],
        [0.75, 0.75],
      ],
    };
    const pipColor = isDark ? "#f3f4f6" : "#111827";
    return (coords[n] || []).map((pair, i) => {
      const [rx, ry] = pair;
      const cx = Math.round(xOffset + rx * (w / 2));
      const cy = Math.round(ry * h);
      return (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={pipR}
          fill={pipColor}
          filter={`url(#${pipFid})`}
        />
      );
    });
  };

  // gradient colors
  const gradStart = isDark ? "#0b1220" : "#ffffff";
  const gradMid = isDark ? "#111827" : "#f8fafc";
  const strokeColor = isDark ? "#374151" : "#cbd5e1";
  const dividerColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";

  const rectProps = vertical
    ? { width: Math.round(h), height: w, rx: 8, ry: 8 }
    : { width: w, height: h, rx: 8, ry: 8 };

  const svgProps = vertical
    ? { svgW: Math.round(h), svgH: w }
    : { svgW: w, svgH: h };

  return (
    <div
      className={className}
      style={{
        width: svgProps.svgW,
        height: svgProps.svgH,
        display: "inline-block",
        ...style,
      }}
    >
      <svg
        width={svgProps.svgW}
        height={svgProps.svgH}
        viewBox={`0 0 ${svgProps.svgW} ${svgProps.svgH}`}
      >
        <defs>
          <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={gradStart} />
            <stop offset="65%" stopColor={gradMid} />
          </linearGradient>

          {/* subtle highlight gradient for a glossy surface */}
          <linearGradient id={hid} x1="0" x2="0" y1="0" y2="1">
            <stop
              offset="0%"
              stopColor={
                isDark ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.9)"
              }
            />
            <stop
              offset="45%"
              stopColor={
                isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.5)"
              }
            />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>

          {/* main soft shadow for the tile */}
          <filter id={fid} x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow
              dx="0"
              dy="5"
              stdDeviation="6"
              floodColor="#000"
              floodOpacity={isDark ? 0.55 : 0.12}
            />
          </filter>

          {/* pip shadow to give pips a lifted/embossed feel */}
          <filter id={pipFid} x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow
              dx="0"
              dy="1.2"
              stdDeviation="1.2"
              floodColor="#000"
              floodOpacity={isDark ? 0.65 : 0.16}
            />
            <feGaussianBlur stdDeviation="0.2" />
          </filter>
        </defs>

        {/* base rect with gradient and soft shadow */}
        <g filter={`url(#${fid})`}>
          <rect
            x="0"
            y="0"
            width={rectProps.width}
            height={rectProps.height}
            rx={rectProps.rx}
            ry={rectProps.ry}
            fill={`url(#${gid})`}
            stroke={strokeColor}
            strokeWidth="1"
          />
        </g>

        {/* inner inset to suggest thickness */}
        <rect
          x="3"
          y="3"
          width={rectProps.width - 6}
          height={rectProps.height - 6}
          rx={Math.max(6, rectProps.rx - 2)}
          ry={Math.max(6, rectProps.ry - 2)}
          fill={isDark ? "rgba(0,0,0,0.06)" : "rgba(0,0,0,0.02)"}
        />

        {/* gloss highlight */}
        <g opacity="0.9">
          <rect
            x="2"
            y="2"
            width={rectProps.width - 4}
            height={Math.round((rectProps.height - 4) * 0.45)}
            rx={Math.max(6, rectProps.rx - 2)}
            ry={Math.max(6, rectProps.ry - 2)}
            fill={`url(#${hid})`}
          />
        </g>

        {/* dividing line */}
        {vertical ? (
          <line
            x1="6"
            y1={Math.floor(rectProps.height / 2)}
            x2={Math.floor(rectProps.width)}
            y2={Math.floor(rectProps.height / 2)}
            stroke={dividerColor}
            strokeWidth="1"
          />
        ) : (
          <line
            x1={Math.floor(rectProps.width / 2)}
            y1="6"
            x2={Math.floor(rectProps.width / 2)}
            y2={rectProps.height - 6}
            stroke={dividerColor}
            strokeWidth="1"
          />
        )}

        {/* pips */}
        {vertical ? (
          <g transform={`translate(0,0)`}>{faceToPips(a, 0)}</g>
        ) : (
          <g>{faceToPips(a, 0)}</g>
        )}

        {vertical ? (
          <g transform={`translate(0,${Math.floor(rectProps.height / 2)})`}>
            {faceToPips(b, 0)}
          </g>
        ) : (
          <g>{faceToPips(b, Math.ceil(rectProps.width / 2))}</g>
        )}
      </svg>
    </div>
  );
}

function DominoesGame({ gameState, gamePlayers = [], user = {}, onGameAction, socket, roomId, isSpectator = false }) {
  // --- Spectator Perspective logic (modular, opt-in, Canasta-style) ---
  const perspectiveOptions = React.useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = React.useState(null);
  const perspectivePlayer = React.useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  React.useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, perspectiveUser, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const tileSize = 72;
  const CHAIN_GAP = 2;
  const CHAIN_EDGE_PADDING = 8;
  const CHAIN_SHADOW_GUTTER = 16;
  const END_SLOT_COUNT = 2;
  const blockedTimerRef = useRef(null);
  const blockedSentRef = useRef(false);
  // previewTileSize removed (was only used for preview)
  // userZoom: controlled by user via buttons. autoScale: computed to fit content. effectiveZoom = autoScale * userZoom
  const [userZoom, setUserZoom] = useState(1);
  const [autoScale, setAutoScale] = useState(1);
  const ZOOM_MIN = 0.6; // min user zoom multiplier
  const ZOOM_MAX = 3.0; // max user zoom multiplier
  const ZOOM_STEP = 0.1;
  const [chainTileSize, setChainTileSize] = useState(tileSize);
  // native horizontal scrolling will be used for panning
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [centerChain, setCenterChain] = useState(false);
  // (collapsed-middle removed) always render full chain

  // group commonly used game-room props for easier passing to child components
  // Perspective-aware helpers
  const matchesPerspectivePlayer = React.useMemo(() => {
    const idSet = new Set((perspectiveIds || []).filter(Boolean).map((value) => String(value)));
    return (player) => {
      if (!player || idSet.size === 0) return false;
      return [player.socketId, player.userId, player.id, player.username]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => idSet.has(String(value)));
    };
  }, [perspectiveIds]);

  // Find the player whose state we are viewing
  const me = React.useMemo(() => {
    // Try to find in gamePlayers, fallback to gameState.players
    const allPlayers = Array.isArray(gamePlayers) && gamePlayers.length > 0 ? gamePlayers : (gameState?.players || []);
    return allPlayers.find(matchesPerspectivePlayer) || null;
  }, [gamePlayers, gameState?.players, matchesPerspectivePlayer]);

  // Chain is global, not per-player
  const chain = React.useMemo(
    () => gameState && gameState.chain ? gameState.chain : [],
    [gameState]
  );
  // Hand is per-player
  const myHand = React.useMemo(
    () => {
      if (!me) return [];
      return gameState && gameState.playerHands && gameState.playerHands[me.socketId]
        ? gameState.playerHands[me.socketId]
        : [];
    }, [gameState, me]
  );
  // Only the perspective player can play
  const canPlay = !!me && gameState && gameState.currentPlayer === me.socketId && !gameState.finished && !isSpectator;

  const ends = useMemo(() => {
    if (!chain.length) return null;
    return { left: chain[0].left, right: chain[chain.length - 1].right };
  }, [chain]);

  const tileMatches = (tile, val) => tile.a === val || tile.b === val;

  // selected tile helper and whether a right-side placeholder is possible
  const selectedTile = selectedIndex !== null ? myHand[selectedIndex] : null;
  const canPlaceLeft = !!(selectedTile && ends && tileMatches(selectedTile, ends.left));
  const canPlaceRight = !!(selectedTile && ends && tileMatches(selectedTile, ends.right));

  // whether the player has any playable tile (if chain is empty, any tile is playable)
  const hasPlayableTile = useMemo(() => {
    if (!myHand || myHand.length === 0) return false;
    if (!ends) return true; // first move: any tile is playable
    return myHand.some(
      (t) => tileMatches(t, ends.left) || tileMatches(t, ends.right)
    );
  }, [myHand, ends]);

  const playTile = (tile, side) => {
    submitAction({ type: "playTile", tile, side }).catch(()=>{});
    setSelectedIndex(null);
  };

  // helper to scroll the container so the tile at chain index `idx` is visible
  // scrollToChainIndex removed (was only used for preview)

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setSelectedIndex(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // keep first tile centered when there is only one tile, otherwise left-justify and allow scroll
  useEffect(() => {
    const el = containerRef.current;
    const inner = innerRef.current;
    if (!el || !inner) return;
    // track when the user is actively interacting (pointer/wheel) so we don't fight their scroll
    const userInteractingRef = { current: false };
    let userInteractTimer = null;
    const markUserInteracting = () => {
      userInteractingRef.current = true;
      if (userInteractTimer) clearTimeout(userInteractTimer);
      userInteractTimer = setTimeout(() => {
        userInteractingRef.current = false;
        userInteractTimer = null;
      }, 600);
    };
    const markUserInteractionEnd = () => {
      userInteractingRef.current = false;
      if (userInteractTimer) {
        clearTimeout(userInteractTimer);
        userInteractTimer = null;
      }
    };

    const compute = () => {
      try {
        // compute available width and the total required width for tiles (including gaps)
        const available = el.clientWidth - 16; // padding
        const count = Math.max(1, chain.length);
        const required =
          (count + END_SLOT_COUNT) * chainTileSize +
          Math.max(0, count - 1) * CHAIN_GAP +
          CHAIN_EDGE_PADDING * 2;
        // if required width exceeds available, compute a scale factor to shrink tiles (but don't go below min)
        const minScale = 28 / chainTileSize;
        const desiredScale = Math.max(
          minScale,
          Math.min(1, available / Math.max(1, required))
        );
        setAutoScale(desiredScale);
        setCenterChain(chain.length === 1 || required <= available);
      } catch (e) {
        if (!userInteractingRef.current) setCenterChain(chain.length === 1);
      }
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    ro.observe(inner);

    // mark user interaction on pointerdown and wheel (these happen before scroll)
    el.addEventListener("pointerdown", markUserInteracting, { passive: true });
    el.addEventListener("wheel", markUserInteracting, { passive: true });
    // native scrolling will handle panning; no pointer handlers required
    // end interaction on pointerup / touchend so we don't hold the flag longer than necessary
    window.addEventListener("pointerup", markUserInteractionEnd, {
      passive: true,
    });
    window.addEventListener("touchend", markUserInteractionEnd, {
      passive: true,
    });
    // scrolling disabled; no scroll listener

    return () => {
      ro.disconnect();
      el.removeEventListener("pointerdown", markUserInteracting);
      el.removeEventListener("wheel", markUserInteracting);
      window.removeEventListener("pointerup", markUserInteractionEnd);
      window.removeEventListener("touchend", markUserInteractionEnd);
      // no scroll listener to remove
      if (userInteractTimer) clearTimeout(userInteractTimer);
    };
  }, [chain, chainTileSize, centerChain, autoScale, userZoom]);

  // responsively shrink chain tiles so full chain fits without horizontal scrollbar
  useEffect(() => {
    const el = containerRef.current;
    const inner = innerRef.current;
    if (!el || !inner) return;
    const computeSize = () => {
      const available = el.clientWidth;
      const padLeft = CHAIN_EDGE_PADDING;
      const padRight = CHAIN_EDGE_PADDING;
      const count = Math.max(1, chain.length);
      const maxTile = Math.floor(
        (available - padLeft - padRight - (count - 1) * CHAIN_GAP) /
          (count + END_SLOT_COUNT)
      );
      const minTile = 28;
      const newSize = Math.max(minTile, Math.min(tileSize, maxTile));
      setChainTileSize(newSize);
    };
    computeSize();
    const ro2 = new ResizeObserver(computeSize);
    ro2.observe(el);
    // also observe inner because extraRightPadding may change and affect layout
    ro2.observe(inner);
    return () => ro2.disconnect();
  }, [chain]);

  // collapsed-center removed: always render full chain

  // chain animations removed to prevent jerky motion; tiles render statically

  // when server marks the game blocked, auto-send endRound after 3 seconds
  // (the UI still shows the message immediately)
  const isBlocked = !!(gameState && gameState.blocked);
  const isFinished = !!(gameState && gameState.finished);

  useEffect(() => {
    // clear any previous timer
    if (blockedTimerRef.current) {
      clearTimeout(blockedTimerRef.current);
      blockedTimerRef.current = null;
    }
    // reset the 'already sent' flag when blocked clears or the round finishes
    if (!isBlocked || isFinished) {
      blockedSentRef.current = false;
    }

    if (isBlocked && !isFinished && !blockedSentRef.current) {
      blockedTimerRef.current = setTimeout(() => {
        try {
          submitAction({ type: "endRound" }).catch(()=>{});
        } catch (e) {}
        // mark that we've attempted to end the round so we don't spam the server
        blockedSentRef.current = true;
        blockedTimerRef.current = null;
      }, 3000);
    }

    return () => {
      if (blockedTimerRef.current) {
        clearTimeout(blockedTimerRef.current);
        blockedTimerRef.current = null;
      }
    };
  }, [isBlocked, isFinished, onGameAction, submitAction]);

  // effective tile size after zoom (autoScale * userZoom)
  const effectiveZoom = Math.max(0.05, autoScale * userZoom);
  const effectiveChainTileSize = Math.max(
    28,
    Math.round(chainTileSize * effectiveZoom)
  );

  const selectedPlacementWidth = selectedTile
    ? selectedTile.a === selectedTile.b
      ? Math.round(effectiveChainTileSize * 0.55)
      : effectiveChainTileSize
    : 0;

  const zoomOverflowGutter = centerChain
    ? 0
    : Math.max(
        CHAIN_SHADOW_GUTTER,
        Math.round(effectiveChainTileSize * 0.24),
        Math.round(Math.max(0, effectiveZoom - 1) * chainTileSize * 0.7)
      );

  // add extra left padding when zoomed in so the left-most tile isn't visually cut off
  const computedLeftPadding = (() => {
    const base = CHAIN_EDGE_PADDING;
    const reservedPlacementWidth = Math.max(
      0,
      effectiveChainTileSize - (canPlaceLeft ? selectedPlacementWidth : 0)
    );
    if (centerChain) return base + reservedPlacementWidth;
    return base + reservedPlacementWidth + zoomOverflowGutter;
  })();

  const computedRightPadding = (() => {
    const base = CHAIN_EDGE_PADDING;
    const reservedPlacementWidth = Math.max(
      0,
      effectiveChainTileSize - (canPlaceRight ? selectedPlacementWidth : 0)
    );
    if (centerChain) return base + reservedPlacementWidth;
    return base + reservedPlacementWidth + zoomOverflowGutter;
  })();

  // track previous zoom so we can preserve scroll center when sizes change
  const prevZoomRef = useRef(effectiveZoom);
  const preserveCenterRatioRef = useRef(null);
  const prevTurnSignatureRef = useRef(null);

  useEffect(() => {
    const turnSignature = `${gameState?.currentPlayer || ""}|${chain.length}|${isFinished ? "done" : "live"}`;
    if (prevTurnSignatureRef.current === null) {
      prevTurnSignatureRef.current = turnSignature;
      return;
    }
    if (prevTurnSignatureRef.current === turnSignature) return;

    prevTurnSignatureRef.current = turnSignature;
    preserveCenterRatioRef.current = null;
    setSelectedIndex(null);
    setUserZoom(1);

    requestAnimationFrame(() => {
      const el = containerRef.current;
      if (!el) return;
      el.scrollLeft = 0;
      requestAnimationFrame(() => {
        el.scrollLeft = 0;
      });
    });
  }, [chain.length, gameState?.currentPlayer, isFinished]);

  // Preserve viewport center when zoom/size changes so scrollbar doesn't lock to left.
  // Use a deterministic geometry-based calculation (tile count, tile size, gap, padding)
  // to avoid racing with browser layout updates (Chrome can update scroll bounds later).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      prevZoomRef.current = effectiveZoom;
      return;
    }
    if (centerChain) {
      prevZoomRef.current = effectiveZoom;
      return;
    }
    const prevZoom = prevZoomRef.current || 1;
    if (prevZoom === effectiveZoom) return;

    // preserved ratio from captureAndZoom (button zooms) wins; otherwise compute from current scroll
    let ratio = preserveCenterRatioRef.current;
    if (ratio == null) {
      const inner = innerRef.current;
      if (!inner) {
        prevZoomRef.current = effectiveZoom;
        return;
      }
      const prevCenterPx = el.scrollLeft + el.clientWidth / 2;
      ratio = inner.scrollWidth > 0 ? prevCenterPx / inner.scrollWidth : 0.5;
    }

    // geometry constants should match how inner is laid out
    const inner = innerRef.current;
    if (!inner) {
      prevZoomRef.current = effectiveZoom;
      return;
    }
    const iw = inner.scrollWidth;
    const cw = el.clientWidth;
    const maxScroll = Math.max(0, iw - cw);
    const newCenterPx = ratio * iw;
    const target = Math.max(
      0,
      Math.min(maxScroll, Math.round(newCenterPx - cw / 2))
    );

    try {
      el.scrollLeft = target;
    } catch (e) {}
    // one rAF reapply as a graceful fallback
    requestAnimationFrame(() => {
      try {
        el.scrollLeft = target;
      } catch (e) {}
    });

    preserveCenterRatioRef.current = null;
    prevZoomRef.current = effectiveZoom;
  }, [
    effectiveZoom,
    effectiveChainTileSize,
    chain.length,
    centerChain,
    computedLeftPadding,
    computedRightPadding,
  ]);

  const zoomIn = () =>
    setUserZoom((z) =>
      Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100)
    );
  const zoomOut = () =>
    setUserZoom((z) =>
      Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100)
    );
  const resetZoom = () => setUserZoom(1);

  // preserve center ratio when user triggers zoom via buttons
  const captureAndZoom = (newZoomSetter) => {
    const el = containerRef.current;
    const inner = innerRef.current;
    if (el && inner && inner.scrollWidth > 0) {
      const centerPx = el.scrollLeft + el.clientWidth / 2;
      preserveCenterRatioRef.current = centerPx / inner.scrollWidth;
    } else {
      preserveCenterRatioRef.current = null;
    }
    newZoomSetter();
  };

  const handleZoomIn = () => captureAndZoom(() => zoomIn());
  const handleZoomOut = () => captureAndZoom(() => zoomOut());
  const handleResetZoom = () => captureAndZoom(() => resetZoom());

  const mySocketId = me?.socketId;
  const scores = gameState?.scores || {};
  const boneyardCount = Array.isArray(gameState?.boneyard) ? gameState.boneyard.length : 0;
  const myPipTotal = React.useMemo(
    () => (myHand || []).reduce((sum, tile) => sum + tile.a + tile.b, 0),
    [myHand]
  );
  const currentPlayerName = React.useMemo(() => {
    const allPlayers = Array.isArray(gamePlayers) && gamePlayers.length > 0 ? gamePlayers : (gameState?.players || []);
    const current = allPlayers.find(
      (player) => player.socketId === gameState?.currentPlayer || player.userId === gameState?.currentPlayer
    );
    return current?.username || null;
  }, [gamePlayers, gameState?.players, gameState?.currentPlayer]);
  const resultWinners = gameState?.result?.winners || gameState?.winners || [];
  const topStatus = isFinished
    ? resultWinners.length
      ? `${gamePlayers.find((player) => resultWinners.includes(player.socketId))?.username || 'Winner'} won the round`
      : 'Round complete'
    : isBlocked
      ? 'Board blocked. Round ending shortly.'
      : canPlay
        ? 'Your turn to play'
        : currentPlayerName
          ? `${currentPlayerName} is thinking`
          : 'Waiting for players';
  const topSubStatus = isFinished
    ? `Chain ${chain.length} tiles • Boneyard ${boneyardCount}`
    : ends
      ? `Open ends ${ends.left} and ${ends.right}`
      : 'Play any tile to start the chain';
  

  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  // If no player is selected (shouldn't happen), show loading
  if (!me) {
    return (
      <div className="mx-auto w-full max-w-2xl space-y-2 p-2 text-slate-800 dark:text-slate-100">
        {showSpectatorPerspectiveSelector && (
          <SpectatorPerspectiveSelector
            players={perspectiveOptions}
            value={spectatorViewPlayerId || ""}
            onChange={setSpectatorViewPlayerId}
          />
        )}
        <div className="p-4 text-slate-600 dark:text-slate-300">Loading Dominoes...</div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-3 text-gray-900 dark:text-gray-100 sm:p-4">
      {showSpectatorPerspectiveSelector && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ""}
          onChange={setSpectatorViewPlayerId}
        />
      )}
    

      

      <div className="rounded-[30px] border border-emerald-200 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.18),transparent_40%),linear-gradient(180deg,#0f5f46_0%,#0b3d31_100%)] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_18px_48px_rgba(0,0,0,0.15)] dark:border-emerald-900/60 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* <div>
            <div className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-100/80">Table</div>

          </div> */}
          <div className="flex flex-wrap items-center gap-2">
            {ends ? (
              <>
                <div className="rounded-full bg-black/20 px-3 py-2 text-sm font-semibold text-emerald-50 shadow-inner">Left: {ends.left}</div>
                <div className="rounded-full bg-black/20 px-3 py-2 text-sm font-semibold text-emerald-50 shadow-inner">Right: {ends.right}</div>
              </>
            ) : (
              <div className="rounded-full bg-black/20 px-3 py-2 text-sm font-semibold text-emerald-50 shadow-inner">Opening move</div>
            )}
            <div className="ml-0 flex items-center gap-2 sm:ml-2">
              <button aria-label="Zoom out" title="Zoom out" onClick={handleZoomOut} className="rounded-xl bg-white/90 px-3 py-2 text-xs font-semibold text-gray-900 shadow-sm dark:bg-white/10 dark:text-gray-100">−</button>
              <button aria-label="Reset zoom" title="Reset zoom" onClick={handleResetZoom} className="rounded-xl bg-white/90 px-3 py-2 text-xs font-semibold text-gray-900 shadow-sm dark:bg-white/10 dark:text-gray-100">Reset Zoom</button>
              <button aria-label="Zoom in" title="Zoom in" onClick={handleZoomIn} className="rounded-xl bg-white/90 px-3 py-2 text-xs font-semibold text-gray-900 shadow-sm dark:bg-white/10 dark:text-gray-100">＋</button>
            </div>
          </div>
        </div>

        {/* Preview removed as requested */}

        <div
          ref={containerRef}
          className="relative mt-4 overflow-x-auto rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
          style={{
            minHeight: tileSize + 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: centerChain ? 'center' : 'flex-start',
          }}
        >
        <div
          ref={innerRef}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: CHAIN_GAP,
            flex: "0 0 auto",
            minWidth: "max-content",
            boxSizing: "border-box",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: computedLeftPadding,
              minWidth: computedLeftPadding,
              height: 1,
              flex: "0 0 auto",
            }}
          />
          {chain.length === 0 ? (
            selectedIndex !== null && myHand[selectedIndex] ? (
              (() => {
                const sel = myHand[selectedIndex];
                const verticalCenter = sel && sel.a === sel.b;
                const centerStyle = verticalCenter
                  ? {
                      width: Math.round(tileSize * 0.55),
                      height: tileSize,
                      borderRadius: 8,
                      border: "3px solid var(--place-color, #3B82F6)",
                    }
                  : {
                      width: tileSize,
                      height: Math.round(tileSize * 0.55),
                      borderRadius: 8,
                      border: "3px solid var(--place-color, #3B82F6)",
                    };
                return (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label="Place center"
                      onClick={() => playTile(sel, "left")}
                      style={centerStyle}
                    />
                  </div>
                );
              })()
            ) : (
              <div className="text-sm text-gray-500">(no tiles)</div>
            )
          ) : (
            (() => {
              const items = [];
              const tile =
                selectedIndex !== null ? myHand[selectedIndex] : null;
              const canLeft = canPlaceLeft;
              const canRight = canPlaceRight;

              const makePlaceholder = (side) => {
                // show blue outline-only placeholder; compute orientation so border dims match tile
                let vertical = !!(tile && tile.a === tile.b);
                // compute style that matches tile rectangle (swap w/h for vertical doubles)
                const phStyle = vertical
                  ? {
                      width: Math.round(effectiveChainTileSize * 0.55),
                      height: effectiveChainTileSize,
                      borderRadius: 8,
                      border: "3px solid #3B82F6",
                      boxSizing: "border-box",
                      background: "transparent",
                    }
                  : {
                      width: effectiveChainTileSize,
                      height: Math.round(effectiveChainTileSize * 0.55),
                      borderRadius: 8,
                      border: "3px solid #3B82F6",
                      boxSizing: "border-box",
                      background: "transparent",
                    };

                return (
                  <div key={"ph-" + side} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`Place ${side}`}
                      onClick={() => playTile(tile, side)}
                      style={{ ...phStyle, boxShadow: '0 0 0 4px rgba(59,130,246,0.15), 0 12px 24px rgba(0,0,0,0.12)' }}
                    />
                  </div>
                );
              };

              if (canLeft) items.push(makePlaceholder("left"));

              // always render full chain
              chain.forEach((c, i) => {
                const isDouble = c.left === c.right;
                if (isDouble) {
                  items.push(
                    <div
                      data-chain-index={i}
                      key={i}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <DominoSVG
                        a={c.left}
                        b={c.right}
                        size={effectiveChainTileSize}
                        vertical
                      />
                    </div>
                  );
                } else {
                  items.push(
                    <div
                      data-chain-index={i}
                      key={i}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <DominoSVG
                        a={c.left}
                        b={c.right}
                        size={effectiveChainTileSize}
                      />
                    </div>
                  );
                }
              });

              if (canRight) items.push(makePlaceholder("right"));

              return items;
            })()
          )}
          <div
            aria-hidden="true"
            style={{
              width: computedRightPadding,
              minWidth: computedRightPadding,
              height: 1,
              flex: "0 0 auto",
            }}
          />
        </div>
      </div>

        {!isFinished && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-black/15 px-4 py-3 text-sm text-emerald-50/85">
            <div>
              {selectedTile ? `Selected ${selectedTile.a}:${selectedTile.b}` : isSpectator ? 'Spectating hand' : 'Select a tile from your hand'}
            </div>
            <div>
              {selectedTile && ends ? `${tileMatches(selectedTile, ends.left) ? 'Playable left' : ''}${tileMatches(selectedTile, ends.left) && tileMatches(selectedTile, ends.right) ? ' • ' : ''}${tileMatches(selectedTile, ends.right) ? 'Playable right' : ''}` : canPlay ? 'Your turn' : isSpectator ? 'Spectating' : 'Waiting'}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-[28px] border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/90 sm:p-5">
        {!isFinished && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">{isSpectator ? "Spectated Hand" : "Your Hand"}</div>
              <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">{isSpectator ? "Viewing this player's hand." : "Tap a tile, then tap the highlighted left or right placement marker on the table."}</div>
            </div>
          </div>
        )}

        <div className="mt-4">
          {myHand.length === 0 ? (
            <div className="text-sm text-gray-500">(no tiles)</div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {myHand.map((t, i) => {
                const isSelected = selectedIndex === i;
                const playable = !ends || tileMatches(t, ends.left) || tileMatches(t, ends.right);
                return (
                  <div
                    key={i}
                    onClick={() => {
                      if (canPlay) setSelectedIndex(i);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    style={{
                      width: tileSize,
                      height: Math.round(tileSize * 0.55),
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: canPlay ? "pointer" : "default",
                      boxSizing: "border-box",
                      borderRadius: 14,
                      background: playable ? 'linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0.04))' : 'linear-gradient(180deg, rgba(148,163,184,0.18), rgba(148,163,184,0.08))',
                      boxShadow: playable ? '0 10px 20px rgba(15,23,42,0.10)' : 'none',
                      ...(isSelected
                        ? {
                            border: "3px solid rgba(96,165,250,0.98)",
                            borderRadius: 10,
                            boxShadow:
                              "0 8px 28px rgba(96,165,250,0.26), inset 0 1px 0 rgba(255,255,255,0.02)",
                            transform: "translateY(-4px)",
                            zIndex: 20,
                            transition:
                              "transform 160ms cubic-bezier(.2,.9,.2,1), box-shadow 160ms, border-color 160ms",
                          }
                        : {}),
                    }}
                  >
                    <DominoSVG a={t.a} b={t.b} size={tileSize} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>


      <div>
        {gameState && gameState.blocked ? (
          <div className="rounded-2xl border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm font-medium text-yellow-700 dark:border-yellow-700/60 dark:bg-yellow-500/10 dark:text-yellow-200">
            No more moves are possible. The round will resolve automatically.
          </div>
        ) : (
          canPlay &&
          !hasPlayableTile && (
            <div className="rounded-2xl border border-slate-200 bg-white/90 px-4 py-4 shadow-sm dark:border-white/10 dark:bg-slate-900/90">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">No playable tiles</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Pass to move the turn on.</div>
                </div>
                <button
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                  disabled={!canPlay}
                  onClick={() => {
                    setSelectedIndex(null);
                    submitAction({ type: "pass" }).catch(()=>{});
                  }}
                >
                  Pass Turn
                </button>
              </div>
            </div>
          )
        )}
      </div>

        <div className="overflow-hidden rounded-[28px] border border-amber-200 bg-gradient-to-br from-amber-100 via-stone-50 to-orange-100 shadow-sm dark:border-amber-900/70 dark:from-gray-900 dark:via-stone-900 dark:to-amber-950/60">
        <div className="p-4 sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">


            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:w-auto">
              <div className="rounded-2xl bg-white/85 px-3 py-3 shadow-sm dark:bg-gray-800/80">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Chain</div>
                <div className="mt-1 text-xl font-bold">{chain.length}</div>
              </div>
              <div className="rounded-2xl bg-white/85 px-3 py-3 shadow-sm dark:bg-gray-800/80">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Boneyard</div>
                <div className="mt-1 text-xl font-bold">{boneyardCount}</div>
              </div>
              <div className="rounded-2xl bg-white/85 px-3 py-3 shadow-sm dark:bg-gray-800/80">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Your Tiles</div>
                <div className="mt-1 text-xl font-bold">{myHand.length}</div>
              </div>
              <div className="rounded-2xl bg-white/85 px-3 py-3 shadow-sm dark:bg-gray-800/80">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Pip Count</div>
                <div className="mt-1 text-xl font-bold">{myPipTotal}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {(gamePlayers || []).map((player, idx) => {
          const playerId = player.socketId;
          const isActive = gameState?.currentPlayer === playerId;
          const isWinner = resultWinners.includes(playerId);
          const isYou = playerId === mySocketId;
          const tilesLeft = (gameState?.playerHands?.[playerId] || []).length;
          return (
            <div
              key={playerId || idx}
              className={`rounded-2xl border px-4 py-4 shadow-sm transition ${isActive ? 'border-amber-400 bg-white dark:border-amber-500 dark:bg-white/10' : 'border-gray-200 bg-white/85 dark:border-white/10 dark:bg-white/5'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">

                  <div>
                    <div className="font-semibold text-gray-900 dark:text-gray-100">{player.username}{isYou ? ' • You' : ''}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{isWinner ? 'Round winner' : isActive ? 'Current turn' : 'Waiting'}</div>
                  </div>
                </div>
                {isActive ? <div className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 dark:bg-amber-500/20 dark:text-amber-200">Turn</div> : null}
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Score</div>
                  <div className="mt-1 text-lg font-bold">{scores[playerId] || 0}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Tiles Left</div>
                  <div className="mt-1 text-lg font-bold">{tilesLeft}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isFinished && gameState?.result ? (
        <div className="rounded-[28px] border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/90 sm:p-5">
          <div className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Round Result</div>
          <div className="mt-2 text-xl font-black text-slate-900 dark:text-slate-100">
            {gamePlayers.find((player) => resultWinners.includes(player.socketId))?.username || 'Winner'} scored {gameState.result.breakdown?.winnerPoints || 0}
          </div>
          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {gameState.result.outcome === 'win' ? 'Points are the sum of the opponents\' remaining pips.' : 'Round complete.'}
          </div>
        </div>
      ) : null}
    </div>
  );
}

DominoesGame.Guide = function DominoesGameGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to play Dominoes</h3>
      <ul className="list-disc ml-6">
        <li>Players take turns placing a domino on the board.</li>
        <li>The domino must touch another domino of the same value.</li>
        <li>First player to place all their dominos wins.</li>
      </ul>
    </div>
  );
};

export default DominoesGame;
